/**
 * Move focus into a card preview's viewer once it exists.
 *
 * Every viewer scopes its Cmd+F to "the keydown happened inside me", and the
 * board deliberately stands down while a preview is open (FS-657) — so if focus
 * stays on the modal, nothing claims the shortcut and the browser's own
 * find-in-page opens instead.
 *
 * Waiting matters: an uncached document renders a loading spinner first, so a
 * single lookup on open finds no focus target and the bug survives for the
 * common case. This watches until the viewer mounts its focusable container.
 *
 * Extracted from BoardCanvas so it can be tested at all — BoardCanvas cannot be
 * rendered in jsdom (tldraw).
 */

/** How long to keep waiting for a viewer that may never load. */
const DEFAULT_TIMEOUT_MS = 15_000

/** Viewers mark their scroll/pan container focusable with tabIndex={0}. */
const VIEWER_SELECTOR = '[tabindex="0"]'

/**
 * Focus the viewer inside `panel` if it is there already.
 * @returns whether a viewer was found (focused, or already holding focus).
 */
export function focusPreviewViewer(panel: HTMLElement | null): boolean {
  const focusable = panel?.querySelector<HTMLElement>(VIEWER_SELECTOR)
  if (!panel || !focusable) return false
  // Never steal focus the user already placed inside the preview — e.g. they
  // clicked the search box while the document was still loading.
  if (panel.contains(document.activeElement)) return true
  focusable.focus({ preventScroll: true })
  return true
}

/**
 * Focus the viewer in `panel` now, or as soon as it mounts.
 * @returns a cleanup function; call it when the preview closes.
 */
export function watchPreviewForViewer(
  panel: HTMLElement | null,
  timeoutMs: number = DEFAULT_TIMEOUT_MS
): () => void {
  if (!panel) return () => {}
  if (focusPreviewViewer(panel)) return () => {}

  const observer = new MutationObserver(() => {
    if (focusPreviewViewer(panel)) observer.disconnect()
  })
  observer.observe(panel, { childList: true, subtree: true })
  const giveUp = setTimeout(() => observer.disconnect(), timeoutMs)

  return () => {
    observer.disconnect()
    clearTimeout(giveUp)
  }
}
